import React, { createContext, useContext, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { findPreviousPath } from "./historyStack";

interface HistoryEntry {
  path: string;
}

interface NavigationHistoryContextType {
  canGoBack: boolean;
  goBack: (fallback?: string) => void;
  removeFromHistory: (url: string) => void;
  removeFromHistoryByPattern: (pattern: (url: string) => boolean) => void;
  goBackWithoutPop: (fallback?: string) => void;
  /** The newest page in the history, the current one included, whose path
   *  `pattern` accepts, or null. Reads the history; navigates nowhere. */
  findPrevious: (pattern: (path: string) => boolean) => string | null;
}

const NavigationHistoryContext =
  createContext<NavigationHistoryContextType | null>(null);

/**
 * Provider that tracks navigation history within the app to enable safe back navigation.
 * It maintains a stack of visited pages and only allows going back through pages we know are safe.
 * Each history entry can carry arbitrary state (e.g. filter/search state) so that navigating
 * back restores the page exactly as the user left it.
 */
export const NavigationHistoryProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Track our own history stack of pages the user has visited
  const historyStack = useRef<HistoryEntry[]>([]);
  const isNavigatingBack = useRef(false);

  useEffect(() => {
    const currentPath = location.pathname + location.search + location.hash;

    if (isNavigatingBack.current) {
      // We initiated this navigation via goBack, don't add to stack
      isNavigatingBack.current = false;
      return;
    }

    // Skip temporary create routes - these get replaced with actual interaction URLs
    if (currentPath.includes("/rms/interactions/create")) {
      return;
    }

    // Skip URLs with docId parameter - these are temporary document view states
    // that shouldn't be added to the navigation history
    if (currentPath.includes("?docId=") || currentPath.includes("&docId=")) {
      return;
    }

    // Check if this is the same as the last entry (prevents duplicates on re-renders)
    const lastEntry = historyStack.current[historyStack.current.length - 1];
    if (lastEntry?.path === currentPath) {
      return;
    }

    // Check if this is just a URL parameter change (like removing ?docId) without actual navigation
    // Compare pathname + hash only, ignoring search parameters
    const currentPathWithoutSearch = location.pathname + location.hash;
    const lastPathWithoutSearch = lastEntry ? lastEntry.path.split("?")[0] : "";

    if (currentPathWithoutSearch === lastPathWithoutSearch && lastEntry) {
      // This is just a parameter change, update the last entry instead of adding new one
      // But only if the current path doesn't have docId (we don't want to update with docId)
      if (
        !currentPath.includes("?docId=") &&
        !currentPath.includes("&docId=")
      ) {
        lastEntry.path = currentPath;
      }
      return;
    }

    // Add current path to our history stack
    historyStack.current.push({ path: currentPath });
  }, [location]);

  const goBack = (fallback: string = "/rms") => {
    // Remove current page from stack
    historyStack.current.pop();

    // Get the previous page
    const previousEntry = historyStack.current[historyStack.current.length - 1];

    if (previousEntry) {
      isNavigatingBack.current = true;
      navigate(previousEntry.path);
    } else {
      // Clear the stack and navigate to fallback
      historyStack.current = [];
      navigate(fallback);
    }
  };

  const goBackWithoutPop = (fallback: string = "/rms") => {
    // Don't pop - just get the previous page (second-to-last item)
    const previousEntry = historyStack.current[historyStack.current.length - 1];

    if (previousEntry) {
      isNavigatingBack.current = true;
      navigate(previousEntry.path);
    } else {
      // Clear the stack and navigate to fallback
      historyStack.current = [];
      navigate(fallback);
    }
  };

  const removeFromHistory = (url: string) => {
    // Remove all occurrences of the URL from the stack
    historyStack.current = historyStack.current.filter(
      (entry) => entry.path !== url
    );
  };

  const removeFromHistoryByPattern = (pattern: (url: string) => boolean) => {
    // Remove all URLs that match the pattern from the stack
    historyStack.current = historyStack.current.filter(
      (entry) => !pattern(entry.path)
    );
  };

  const findPrevious = (pattern: (path: string) => boolean): string | null =>
    findPreviousPath(
      historyStack.current.map((entry) => entry.path),
      pattern
    );

  const canGoBack = historyStack.current.length > 1;

  return (
    <NavigationHistoryContext.Provider
      value={{
        canGoBack,
        goBack,
        removeFromHistory,
        removeFromHistoryByPattern,
        goBackWithoutPop,
        findPrevious,
      }}
    >
      {children}
    </NavigationHistoryContext.Provider>
  );
};

/**
 * Hook to access navigation history functions
 */
export const useNavigationHistory = () => {
  const context = useContext(NavigationHistoryContext);
  if (!context) {
    throw new Error(
      "useNavigationHistory must be used within NavigationHistoryProvider"
    );
  }
  return context;
};
